import {
  formatEcho,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/physics-fields/format";
import type {
  ForceAreaSolution,
  ForceAreaTarget,
  HydrostaticSolution,
  HydrostaticTarget,
  PressureBasis,
} from "@/lib/tool-kit/physics-fields/pressure";

/** Equation, substitution and answer in SI. Symbols are not translated. */

export function forceAreaWorking(
  target: ForceAreaTarget,
  s: ForceAreaSolution,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const e = (v: number) => formatEcho(fmt, v);
  const r = (v: number, unit: string) => `${formatSig(fmt, v, sig)} ${unit}`;
  if (target === "force") {
    return [
      "F = P A",
      `F = ${e(s.pressure)} × ${e(s.area)} = ${r(s.force, "N")}`,
    ];
  }
  if (target === "area") {
    return [
      "A = F / P",
      `A = ${e(s.force)} / ${e(s.pressure)} = ${r(s.area, "m²")}`,
    ];
  }
  return [
    "P = F / A",
    `P = ${e(s.force)} / ${e(s.area)} = ${r(s.pressure, "Pa")}`,
  ];
}

export function hydrostaticWorking(
  target: HydrostaticTarget,
  basis: PressureBasis,
  enteredPressure: number | null,
  s: HydrostaticSolution,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const e = (v: number) => formatEcho(fmt, v);
  const r = (v: number, unit: string) => `${formatSig(fmt, v, sig)} ${unit}`;
  const lines: string[] = [];
  if (
    target !== "pressure" &&
    basis === "absolute" &&
    enteredPressure !== null
  ) {
    lines.push(
      `P_gauge = P_abs − P₀ = ${e(enteredPressure)} − ${e(s.surface)} = ${r(s.gauge, "Pa")}`
    );
  }
  if (target === "depth") {
    lines.push(
      "h = P_gauge / (ρ g)",
      `h = ${e(s.gauge)} / (${e(s.density)} × ${e(s.gravity)}) = ${r(s.depth, "m")}`
    );
  } else if (target === "density") {
    lines.push(
      "ρ = P_gauge / (g h)",
      `ρ = ${e(s.gauge)} / (${e(s.gravity)} × ${e(s.depth)}) = ${r(s.density, "kg/m³")}`
    );
  } else {
    lines.push(
      "P_gauge = ρ g h",
      `P_gauge = ${e(s.density)} × ${e(s.gravity)} × ${e(s.depth)} = ${r(s.gauge, "Pa")}`
    );
  }
  lines.push(
    "P_abs = P₀ + ρ g h",
    `P_abs = ${e(s.surface)} + ${e(s.gauge)} = ${r(s.absolute, "Pa")}`
  );
  return lines;
}
